import { useState, useEffect, useCallback } from "react";
import api, { errMsg } from "@/lib/api";
import { Eyebrow } from "@/components/Eyebrow";
import { Loader2, RefreshCw, Building2, Users, Target, Heart, Lightbulb, AlertCircle, ArrowRight } from "lucide-react";

const LABEL = { fontSize: 11, letterSpacing: "0.08em", textTransform: "uppercase", color: "var(--tmuted)", fontWeight: 600 };

export function MarketResearchPanel({ brandId, initialResearch, onUseAngle }) {
  const [research, setResearch] = useState(initialResearch || null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const run = useCallback(async (force) => {
    if (!brandId) return;
    setLoading(true);
    setError("");
    try {
      const { data } = await api.post("/brand/research", { brandId, force: !!force });
      setResearch(data.research);
    } catch (e) {
      setError(errMsg(e, "Could not generate market research"));
    } finally {
      setLoading(false);
    }
  }, [brandId]);

  useEffect(() => {
    setResearch(initialResearch || null);
    if (brandId && !initialResearch) run(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brandId]);

  const icp = research?.icp || {};
  const competitors = research?.competitors || [];
  const angles = research?.angles || [];

  return (
    <div className="panel-card" style={{ padding: 22 }} data-testid="market-research-panel">
      <div className="flex items-start justify-between gap-3">
        <div>
          <Eyebrow testId="eyebrow-research">Market Research</Eyebrow>
          <p className="font-body" style={{ fontSize: 12.5, color: "var(--tsecondary)", margin: "6px 0 0" }}>
            Industry &amp; competitor analysis · ICP pain map · differentiated content angles
          </p>
        </div>
        {research && !loading && (
          <button onClick={() => run(true)} className="btn-secondary font-mono" data-testid="research-rerun" style={{ fontSize: 11, padding: "7px 11px", flexShrink: 0 }}>
            <RefreshCw size={12} /> Re-run
          </button>
        )}
      </div>

      {loading && (
        <div className="flex items-center gap-3" data-testid="research-loading" style={{ padding: "26px 4px", color: "var(--tsecondary)" }}>
          <Loader2 size={18} className="spin" />
          <span className="font-body" style={{ fontSize: 13.5 }}>Researching the industry, competitors &amp; ideal customer…</span>
        </div>
      )}

      {!loading && error && (
        <div data-testid="research-error" style={{ marginTop: 16 }}>
          <div className="flex items-center gap-2 font-body" style={{ fontSize: 13, color: "var(--err)" }}>
            <AlertCircle size={15} /> {error}
          </div>
          <button onClick={() => run(true)} className="btn-secondary font-mono" style={{ fontSize: 11, padding: "7px 11px", marginTop: 12 }}>
            <RefreshCw size={12} /> Try again
          </button>
        </div>
      )}

      {!loading && research && (
        <div style={{ marginTop: 20, display: "flex", flexDirection: "column", gap: 22 }}>
          {/* Industry */}
          {research.industry && (
            <div data-testid="research-industry">
              <div className="flex items-center gap-2" style={LABEL}><Building2 size={13} /> Industry</div>
              <p className="font-body" style={{ fontSize: 14, color: "var(--tprimary)", margin: "8px 0 0" }}>{research.industry}</p>
            </div>
          )}

          {/* Competitors */}
          {competitors.length > 0 && (
            <div data-testid="research-competitors">
              <div className="flex items-center gap-2" style={LABEL}><Users size={13} /> Competitors</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 10 }}>
                {competitors.map((c, i) => (
                  <div key={i} style={{ padding: "10px 12px", borderRadius: 9, border: "1px solid var(--cardborder)", background: "var(--surface)" }}>
                    <div className="font-display" style={{ fontSize: 13.5, fontWeight: 600, color: "var(--tprimary)" }}>{c.name}</div>
                    {c.positioning && <div className="font-body" style={{ fontSize: 12.5, color: "var(--tsecondary)", marginTop: 2 }}>{c.positioning}</div>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ICP pain map */}
          {(icp.persona || (icp.pains || []).length > 0) && (
            <div data-testid="research-icp">
              <div className="flex items-center gap-2" style={LABEL}><Target size={13} /> ICP Pain Map</div>
              {icp.persona && <p className="font-body" style={{ fontSize: 13.5, color: "var(--tprimary)", margin: "8px 0 12px", lineHeight: 1.5 }}>{icp.persona}</p>}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {(icp.pains || []).length > 0 && (
                  <div>
                    <div className="flex items-center gap-1.5 font-mono" style={{ fontSize: 10.5, color: "var(--err)", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 7 }}><AlertCircle size={12} /> Pains</div>
                    <ul style={{ margin: 0, paddingLeft: 16, display: "flex", flexDirection: "column", gap: 5 }}>
                      {icp.pains.map((p, i) => <li key={i} className="font-body" style={{ fontSize: 13, color: "var(--tsecondary)", lineHeight: 1.45 }}>{p}</li>)}
                    </ul>
                  </div>
                )}
                {(icp.desires || []).length > 0 && (
                  <div>
                    <div className="flex items-center gap-1.5 font-mono" style={{ fontSize: 10.5, color: "var(--ok)", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 7 }}><Heart size={12} /> Desired outcomes</div>
                    <ul style={{ margin: 0, paddingLeft: 16, display: "flex", flexDirection: "column", gap: 5 }}>
                      {icp.desires.map((d, i) => <li key={i} className="font-body" style={{ fontSize: 13, color: "var(--tsecondary)", lineHeight: 1.45 }}>{d}</li>)}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Content angles */}
          {angles.length > 0 && (
            <div data-testid="research-angles">
              <div className="flex items-center gap-2" style={LABEL}><Lightbulb size={13} /> Differentiated content angles</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 9, marginTop: 10 }}>
                {angles.map((a, i) => (
                  <div key={i} data-testid={`research-angle-${i}`} style={{ display: "flex", gap: 11, padding: "11px 13px", borderRadius: 9, border: "1px solid var(--cardborder)", background: "var(--panel)" }}>
                    <span className="font-mono" style={{ fontSize: 12, fontWeight: 700, color: "var(--orange)", flexShrink: 0, width: 18 }}>{String(i + 1).padStart(2, "0")}</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div className="font-display" style={{ fontSize: 13.5, fontWeight: 600, color: "var(--tprimary)" }}>{a.title}</div>
                      {a.angle && <div className="font-body" style={{ fontSize: 12.5, color: "var(--tsecondary)", marginTop: 2, lineHeight: 1.45 }}>{a.angle}</div>}
                    </div>
                    {onUseAngle && (
                      <button
                        data-testid={`use-angle-${i}`}
                        onClick={() => onUseAngle([a.title, a.angle].filter(Boolean).join(" — "))}
                        className="btn-secondary font-mono"
                        title="Use this angle in Create"
                        style={{ fontSize: 10.5, padding: "6px 10px", flexShrink: 0, alignSelf: "center", whiteSpace: "nowrap" }}
                      >
                        Use this angle <ArrowRight size={12} />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
